/**
 * PPJK realtime events: a tiny in-app bus fed by the per-PPJK websocket.
 *
 * The websocket (useCeisaWebSocket, opened once by CeisaNotificationProvider)
 * publishes the contract §8 messages here. Payloads carry no business data:
 * a subscriber re-reads whatever it shows through the authenticated APIs.
 *
 *   statement_config_changed   { at }            after any Pernyataan write for this PPJK
 *   consignee_master_changed   { at, reason }    after any consignee write for this PPJK
 *   eksportir_master_changed   { at, reason }    after any exportir write for this PPJK
 *   master_barang_changed      { at, reason, importerNpwp }  after any Master Barang write for this PPJK
 *
 * API
 *   subscribePpjkEvent(type, handler) -> unsubscribe
 *       Plain subscription, usable outside React.
 *   usePpjkEvent(type, handler)
 *       React hook; subscribes for the component's lifetime and always calls
 *       the latest handler (no need to memoise it).
 *   publishPpjkEvent(type, data) -> boolean
 *       Used by the websocket consumer. Unknown types are ignored (returns
 *       false). A handler that throws does not stop the others.
 *
 * Events reach this tab for writes made anywhere, this tab included, so a
 * handler that reacts to "a colleague changed it" must account for its own
 * writes (e.g. ignore while a save is in flight, or compare with what it sent).
 *
 * consignee_master_changed, eksportir_master_changed and master_barang_changed
 * also invalidate their query families centrally (usePpjkRealtimeInvalidation), so readers need no
 * handler just to stay current.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { consigneeKeys } from '@/features/consignee-import/keys';
import { eksportirKeys } from '@/features/eksportir-master/keys';
import { masterBarangKeys } from '@/features/master-barang/keys';

export interface PpjkEventPayloads {
  statement_config_changed: { at?: string };
  consignee_master_changed: { at?: string; reason?: 'manual' | 'import' | 'delete' | 'submission' | string };
  eksportir_master_changed: { at?: string; reason?: 'manual' | 'import' | 'delete' | 'submission' | string };
  master_barang_changed: {
    at?: string;
    reason?: 'create' | 'update' | 'delete' | 'import' | 'backfill' | string;
    importerNpwp?: string;
  };
}

export type PpjkEventType = keyof PpjkEventPayloads;

export const PPJK_EVENT_TYPES: readonly PpjkEventType[] = [
  'statement_config_changed',
  'consignee_master_changed',
  'eksportir_master_changed',
  'master_barang_changed',
];

export const isPpjkEventType = (type: unknown): type is PpjkEventType =>
  typeof type === 'string' && (PPJK_EVENT_TYPES as readonly string[]).includes(type);

type Handler<T extends PpjkEventType> = (data: PpjkEventPayloads[T]) => void;

const handlers = new Map<PpjkEventType, Set<Handler<PpjkEventType>>>();

export function subscribePpjkEvent<T extends PpjkEventType>(type: T, handler: Handler<T>): () => void {
  let set = handlers.get(type);
  if (!set) {
    set = new Set();
    handlers.set(type, set);
  }
  const entry = handler as Handler<PpjkEventType>;
  set.add(entry);
  return () => {
    handlers.get(type)?.delete(entry);
  };
}

export function publishPpjkEvent(type: string, data?: unknown): boolean {
  if (!isPpjkEventType(type)) return false;
  const payload = (data && typeof data === 'object' ? data : {}) as PpjkEventPayloads[typeof type];
  for (const handler of [...(handlers.get(type) ?? [])]) {
    try {
      handler(payload);
    } catch (error) {
      console.warn(`[realtime] ${type} handler failed`, error);
    }
  }
  return true;
}

export function usePpjkEvent<T extends PpjkEventType>(type: T, handler: Handler<T>): void {
  const latest = useRef(handler);
  latest.current = handler;
  useEffect(() => subscribePpjkEvent(type, (data) => latest.current(data)), [type]);
}

/** Mounted once next to the websocket: keeps the consignee, exportir and Master Barang queries current for every reader. */
export function usePpjkRealtimeInvalidation(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    const unsubscribeConsignee = subscribePpjkEvent('consignee_master_changed', () => {
      void queryClient.invalidateQueries({ queryKey: consigneeKeys.all });
    });
    const unsubscribeEksportir = subscribePpjkEvent('eksportir_master_changed', () => {
      void queryClient.invalidateQueries({ queryKey: eksportirKeys.all });
    });
    const unsubscribeMasterBarang = subscribePpjkEvent('master_barang_changed', () => {
      void queryClient.invalidateQueries({ queryKey: masterBarangKeys.all });
    });
    return () => {
      unsubscribeConsignee();
      unsubscribeEksportir();
      unsubscribeMasterBarang();
    };
  }, [queryClient]);
}

/** Test-only: drop every subscription. */
export const __resetPpjkEvents = () => handlers.clear();
